import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { momentLocaleOf } from "../../../../utils/momentLocale";
import { AiOutlineLoading } from "react-icons/ai";
import {
  HiCheck,
  HiCurrencyDollar,
  HiXCircle,
  HiReply,
  HiCreditCard,
} from "react-icons/hi";
import { Select, TextInput } from "flowbite-react";
import { ModalShell } from "../../../atoms/ModalShell";
import { InvoiceResponseDTO } from "../../../../services/paymentModule";
import PaymentModuleService from "../../../../services/paymentModule";
import { toast } from "react-toastify";
import { CustomButton } from "../../../atoms/CustomButton";
import { EmptyState } from "../../../atoms/EmptyState";
import ActionButton from "../../../atoms/ActionButton";
import { useCopyToClipboard } from "../../../atoms/useCopyToClipboard";
import { usePermissions } from "../../../../utils/permissions";
import useAuth from "../../../../hooks/useAuth";
import { Cap, Level } from "../../../../utils/rbacCatalog";
import PaymentStatusSummary from "./PaymentStatusSummary";
import PlanTimelineDrawer from "./PlanTimelineDrawer";
import { FieldLabel } from "../../../atoms/FieldLabel";
import {
  CancelSubscriptionInfo,
  InvoiceLinkVariant,
  PaymentHistoryGroup,
  formatAmount,
  isRecurringPlan,
  summarizePlans,
} from "./paymentHistoryUtils";

const HIGHLIGHT_DURATION_MS = 3000;

interface UserPaymentHistoryProps {
  customerIdentification: string;
  entityIdentification: string;
  userIdentification?: string;
  paymentHistory: PaymentHistoryGroup[];
  isLoading: boolean;
  isError: boolean;
  refetch?: () => void;
  highlightedLink?: string | null;
  onHighlightLink?: (link: string | null) => void;
  onCancelSubscription?: (info: CancelSubscriptionInfo) => void;
  existingPaymentLinks?: string[];
}

interface InvoiceLink {
  [invoiceIdAndVariant: string]: string | null;
}

const UserPaymentHistory: React.FC<UserPaymentHistoryProps> = ({
  customerIdentification,
  entityIdentification,
  userIdentification,
  paymentHistory,
  isLoading,
  isError,
  refetch,
  onHighlightLink,
  onCancelSubscription,
  existingPaymentLinks,
}) => {
  const { t, i18n } = useTranslation();
  const [invoiceLinks, setInvoiceLinks] = useState<InvoiceLink>({});
  const highlightTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const [showRefundModal, setShowRefundModal] = useState(false);
  const [refundingPayment, setRefundingPayment] = useState<{
    paymentStepIdentification: string;
    amount: number;
    currency: string;
    description: string;
  } | null>(null);
  const [refundOption, setRefundOption] = useState<"FULL" | "PARTIAL">("FULL");
  const [refundAmount, setRefundAmount] = useState("");
  const [refundReason, setRefundReason] = useState("");
  const [isRefunding, setIsRefunding] = useState(false);
  const [refundFormTouched, setRefundFormTouched] = useState(false);

  moment.locale(momentLocaleOf(i18n.language));

  const { user } = useAuth();
  const permissions = usePermissions(user);
  const canCreatePaymentLink = permissions.can(
    Cap.CREATE_PAYMENT_LINK,
    Level.WRITE
  );

  useEffect(() => {
    return () => {
      if (highlightTimeoutRef.current) {
        clearTimeout(highlightTimeoutRef.current);
      }
    };
  }, []);

  const handlePaymentRequestClick = useCallback(
    (paymentRequestLink: string) => {
      if (!canCreatePaymentLink) return;
      if (onHighlightLink) {
        onHighlightLink(paymentRequestLink);
      }

      const paymentLinksSection = document.querySelector(
        "[data-payment-links-section]"
      );
      if (paymentLinksSection) {
        paymentLinksSection.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }

      const paymentLinkCard = document.querySelector(
        `[data-payment-link="${paymentRequestLink}"]`
      );
      if (paymentLinkCard) {
        paymentLinkCard.scrollIntoView({ behavior: "smooth", block: "center" });
      }

      if (highlightTimeoutRef.current) {
        clearTimeout(highlightTimeoutRef.current);
      }

      highlightTimeoutRef.current = setTimeout(() => {
        if (onHighlightLink) {
          onHighlightLink(null);
        }
      }, HIGHLIGHT_DURATION_MS);
    },
    [onHighlightLink]
  );

  const invoiceFetchPromisesRef = useRef<
    Map<string, Promise<InvoiceResponseDTO | null>>
  >(new Map());

  const fetchInvoice = useCallback(
    (invoiceId: string): Promise<InvoiceResponseDTO | null> => {
      const cached = invoiceFetchPromisesRef.current.get(invoiceId);
      if (cached) return cached;

      const request = (async () => {
        try {
          const response = await PaymentModuleService.getInvoiceLink(
            customerIdentification,
            entityIdentification,
            invoiceId
          );

          const responseData = response.data;
          const invoiceData = responseData?.element;

          if (responseData?.code === "OK" && invoiceData) {
            return invoiceData;
          }
          invoiceFetchPromisesRef.current.delete(invoiceId);
          toast.error(
            responseData?.message ||
              (t(
                "paymentModule.userPaymentHistory.invoiceLinkErrorGeneric"
              ) as string)
          );
          return null;
        } catch (error) {
          console.error("Error fetching invoice link:", error);
          invoiceFetchPromisesRef.current.delete(invoiceId);
          toast.error(
            t(
              "paymentModule.userPaymentHistory.invoiceLinkErrorGeneric"
            ) as string
          );
          throw error;
        }
      })();

      invoiceFetchPromisesRef.current.set(invoiceId, request);
      return request;
    },
    [customerIdentification, entityIdentification, t]
  );

  const fetchInvoiceLink = useCallback(
    async (
      invoiceId: string,
      variant: InvoiceLinkVariant
    ): Promise<string | null> => {
      const invoice = await fetchInvoice(invoiceId);
      const link =
        variant === "payment"
          ? invoice?.invoiceUrlLink || invoice?.invoicePDFLink
          : invoice?.invoicePDFLink || invoice?.invoiceUrlLink;
      if (link) {
        setInvoiceLinks((prev) => ({
          ...prev,
          [`${invoiceId}:${variant}`]: link,
        }));
      }
      return link || null;
    },
    [fetchInvoice]
  );

  const handleOpenRefund = useCallback(
    (
      paymentStepIdentification: string,
      amount: number,
      currency: string,
      description: string
    ) => {
      if (!canCreatePaymentLink) return;
      setRefundingPayment({
        paymentStepIdentification,
        amount,
        currency,
        description,
      });
      setRefundOption("FULL");
      setRefundAmount("");
      setRefundReason("");
      setRefundFormTouched(false);
      setShowRefundModal(true);
    },
    []
  );

  const handleRefund = useCallback(async () => {
    if (!refundingPayment || !canCreatePaymentLink) return;

    setRefundFormTouched(true);

    let hasError = false;

    if (!refundReason) {
      hasError = true;
    }

    if (refundOption === "PARTIAL") {
      const amountCents = Math.round(parseFloat(refundAmount) * 100);
      if (
        !amountCents ||
        amountCents <= 0 ||
        amountCents > refundingPayment.amount
      ) {
        hasError = true;
      }
    }

    if (hasError) return;

    try {
      setIsRefunding(true);
      const data: any = {
        payment_step_identification: refundingPayment.paymentStepIdentification,
        refund_option: refundOption,
      };
      if (refundOption === "PARTIAL") {
        data.amount = Math.round(parseFloat(refundAmount) * 100);
      }
      data.reason = refundReason;

      await PaymentModuleService.refundPayment(
        customerIdentification,
        entityIdentification,
        data
      );

      toast.success(
        refundOption === "FULL"
          ? t("paymentModule.userPaymentHistory.fullRefundSuccess")
          : t("paymentModule.userPaymentHistory.partialRefundSuccess")
      );
      setShowRefundModal(false);
      setRefundingPayment(null);
      if (refetch) refetch();
    } catch (err: any) {
      toast.error(
        err.response?.data?.message ||
          t("paymentModule.userPaymentHistory.refundFailed")
      );
    } finally {
      setIsRefunding(false);
    }
  }, [
    refundingPayment,
    refundOption,
    refundAmount,
    refundReason,
    customerIdentification,
    entityIdentification,
    refetch,
    t,
  ]);

  const groupsByCode = useMemo(() => {
    const map = new Map<string, PaymentHistoryGroup>();
    paymentHistory.forEach((g) => map.set(g.planCode, g));
    return map;
  }, [paymentHistory]);

  const [openPlanCode, setOpenPlanCode] = useState<string | null>(null);

  const handleOpenPaymentRequestFromDrawer = useCallback(
    (paymentRequestLink: string) => {
      setOpenPlanCode(null);
      handlePaymentRequestClick(paymentRequestLink);
    },
    [handlePaymentRequestClick]
  );

  const totalPayments = useMemo(
    () =>
      summarizePlans(paymentHistory).reduce((sum, p) => sum + p.movements, 0),
    [paymentHistory]
  );

  const canOfferCardUpdate = useMemo(
    () =>
      paymentHistory.some(
        (g) =>
          isRecurringPlan(g.payment_type) &&
          g.paymentSteps.some((p) =>
            ["ACTIVE", "UNPAID", "ERROR"].includes(p.paymentStatusEnum)
          )
      ),
    [paymentHistory]
  );

  const [isBuildingCardUpdateLink, setIsBuildingCardUpdateLink] =
    useState(false);
  const { status: cardCopyStatus, copy: copyCardUpdateLink } =
    useCopyToClipboard({
      onError: (err: any) =>
        toast.error(
          err?.response?.data?.message ||
            (t("paymentModule.userPaymentHistory.copyCardLinkFailed") as string)
        ),
    });
  const cardLinkCopied = cardCopyStatus === "success";

  const handleCopyCardUpdateLink = useCallback(async () => {
    if (!userIdentification || !canOfferCardUpdate || !canCreatePaymentLink)
      return;
    setIsBuildingCardUpdateLink(true);
    try {
      await copyCardUpdateLink(async () => {
        const response = await PaymentModuleService.getCardUpdateLink(
          customerIdentification,
          entityIdentification,
          userIdentification
        );
        return response.data?.element?.link;
      });
    } finally {
      setIsBuildingCardUpdateLink(false);
    }
  }, [
    customerIdentification,
    entityIdentification,
    userIdentification,
    canOfferCardUpdate,
    canCreatePaymentLink,
    copyCardUpdateLink,
  ]);

  if (isLoading) {
    return (
      <div className="flex flex-col gap-4 w-full">
        <div className="flex items-center gap-3 border-b border-gray-100 pb-3">
          <h2 className="text-sm font-semibold text-gray-900">
            {t("paymentHistory.title")}
          </h2>
        </div>
        <div className="flex items-center justify-center py-12">
          <AiOutlineLoading className="h-8 w-8 animate-spin text-blue-500" />
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-col gap-4 w-full">
        <div className="flex items-center gap-3 border-b border-gray-100 pb-3">
          <h2 className="text-sm font-semibold text-gray-900">
            {t("paymentHistory.title")}
          </h2>
        </div>
        <div className="flex flex-col items-center justify-center py-12 bg-red-50 rounded-lg border border-red-100">
          <HiXCircle className="h-12 w-12 text-red-500 mb-3" />
          <p className="text-red-700 font-medium mb-4">
            {t("paymentHistory.error")}
          </p>
          {refetch && (
            <CustomButton
              onClick={refetch}
              title={t("common.retry")}
              variant="secondary"
            />
          )}
        </div>
      </div>
    );
  }

  if (!paymentHistory || paymentHistory.length === 0 || totalPayments === 0) {
    return (
      <div className="flex flex-col gap-4 w-full">
        <div className="flex items-center gap-3 border-b border-gray-100 pb-3">
          <h2 className="text-sm font-semibold text-gray-900">
            {t("paymentHistory.title")}
          </h2>
        </div>
        <EmptyState
          icon={<HiCurrencyDollar size={24} />}
          title={t("paymentHistory.empty")}
          description={t("paymentHistory.emptyDescription")}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 w-full">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 pb-3">
        <div className="flex items-baseline gap-3">
          <h2 className="text-sm font-semibold text-gray-900">
            {t("paymentHistory.title")}
          </h2>
          <span className="text-sm text-gray-500 tabular-nums">
            {t("paymentHistory.paymentsCount", { count: totalPayments })}
          </span>
        </div>
        {userIdentification && canOfferCardUpdate && canCreatePaymentLink && (
          <span
            title={
              t("paymentModule.userPaymentHistory.copyCardLinkHint") as string
            }
          >
            <ActionButton
              onClick={handleCopyCardUpdateLink}
              isLoading={isBuildingCardUpdateLink}
              disabled={!canCreatePaymentLink}
              colorScheme={cardLinkCopied ? "green" : "blue"}
              title={
                <span className="inline-flex items-center gap-1.5">
                  {cardLinkCopied ? (
                    <HiCheck size={14} />
                  ) : (
                    <HiCreditCard size={14} />
                  )}
                  <span>
                    {cardLinkCopied
                      ? t("common.copied")
                      : t("paymentModule.userPaymentHistory.copyCardLink")}
                  </span>
                </span>
              }
              aria-label={
                t("paymentModule.userPaymentHistory.copyCardLinkHint") as string
              }
            />
          </span>
        )}
      </div>

      <PaymentStatusSummary
        paymentHistory={paymentHistory}
        onOpenPlan={setOpenPlanCode}
      />

      <PlanTimelineDrawer
        open={openPlanCode !== null}
        onClose={() => setOpenPlanCode(null)}
        group={openPlanCode ? groupsByCode.get(openPlanCode) || null : null}
        canCreatePaymentLink={canCreatePaymentLink}
        invoiceLinks={invoiceLinks}
        onFetchInvoiceLink={fetchInvoiceLink}
        onOpenRefund={handleOpenRefund}
        onCancelSubscription={onCancelSubscription}
        onPaymentRequestClick={handleOpenPaymentRequestFromDrawer}
        existingPaymentLinks={existingPaymentLinks}
        onCopyCardUpdateLink={handleCopyCardUpdateLink}
        isCopyingCardUpdateLink={isBuildingCardUpdateLink}
        cardLinkJustCopied={cardLinkCopied}
        canCopyCardUpdateLink={
          !!userIdentification && canOfferCardUpdate && canCreatePaymentLink
        }
      />

      <ModalShell
        show={showRefundModal}
        onClose={() => {
          setShowRefundModal(false);
          setRefundingPayment(null);
        }}
        size="md"
        title={
          <span className="flex min-w-0 items-center gap-3">
            <HiReply className="flex-shrink-0 text-red-600" size={24} />
            <span className="truncate">
              {t("paymentModule.userPaymentHistory.modalTitle")}
            </span>
          </span>
        }
        footer={
          <>
            <CustomButton
              title={t("common.cancel") as string}
              onClick={() => {
                setShowRefundModal(false);
                setRefundingPayment(null);
              }}
              variant="secondary"
              disabled={isRefunding}
            />
            <CustomButton
              title={
                isRefunding
                  ? (t("paymentModule.userPaymentHistory.processing") as string)
                  : refundOption === "FULL"
                  ? (t(
                      "paymentModule.userPaymentHistory.refundFullAmount"
                    ) as string)
                  : (t(
                      "paymentModule.userPaymentHistory.refundButton"
                    ) as string)
              }
              onClick={handleRefund}
              variant="danger"
              disabled={isRefunding}
            />
          </>
        }
      >
        {refundingPayment && (
          <div className="space-y-4">
            <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
              <p className="text-sm font-medium text-gray-700">
                {refundingPayment.description}
              </p>
              <p className="text-lg font-bold text-gray-900 mt-1">
                {formatAmount(
                  refundingPayment.amount,
                  refundingPayment.currency
                )}
              </p>
            </div>

            <div>
              <FieldLabel
                value={
                  t(
                    "paymentModule.userPaymentHistory.refundTypeLabel"
                  ) as string
                }
              />
              <div className="flex gap-3 mt-1">
                <button
                  onClick={() => {
                    setRefundOption("FULL");
                    setRefundAmount("");
                  }}
                  className={`flex-1 p-3 rounded-lg border-2 text-sm font-medium transition-all ${
                    refundOption === "FULL"
                      ? "border-red-500 bg-red-50 text-red-700"
                      : "border-gray-200 bg-white text-gray-600 hover:border-gray-300"
                  }`}
                >
                  {t("paymentModule.userPaymentHistory.fullRefund")}
                </button>
                <button
                  onClick={() => setRefundOption("PARTIAL")}
                  className={`flex-1 p-3 rounded-lg border-2 text-sm font-medium transition-all ${
                    refundOption === "PARTIAL"
                      ? "border-red-500 bg-red-50 text-red-700"
                      : "border-gray-200 bg-white text-gray-600 hover:border-gray-300"
                  }`}
                >
                  {t("paymentModule.userPaymentHistory.partialRefund")}
                </button>
              </div>
            </div>

            {refundOption === "PARTIAL" && (
              <div>
                <FieldLabel
                  htmlFor="refund-amount"
                  value={
                    t(
                      "paymentModule.userPaymentHistory.refundAmountLabel"
                    ) as string
                  }
                />
                <TextInput
                  id="refund-amount"
                  type="number"
                  placeholder={
                    t(
                      "paymentModule.userPaymentHistory.refundAmountPlaceholder"
                    ) as string
                  }
                  value={refundAmount}
                  onChange={(e) => setRefundAmount(e.target.value)}
                  min={0.01}
                  max={refundingPayment.amount / 100}
                  step={0.01}
                  color={
                    refundFormTouched &&
                    (!refundAmount ||
                      parseFloat(refundAmount) <= 0 ||
                      Math.round(parseFloat(refundAmount) * 100) >
                        refundingPayment.amount)
                      ? "failure"
                      : undefined
                  }
                />
                {refundFormTouched &&
                (!refundAmount || parseFloat(refundAmount) <= 0) ? (
                  <p className="mt-1 text-xs text-red-600">
                    {t("paymentModule.userPaymentHistory.amountRequired")}
                  </p>
                ) : refundFormTouched &&
                  Math.round(parseFloat(refundAmount) * 100) >
                    refundingPayment.amount ? (
                  <p className="mt-1 text-xs text-red-600">
                    {t("paymentModule.userPaymentHistory.amountExceeds", {
                      max: formatAmount(
                        refundingPayment.amount,
                        refundingPayment.currency
                      ),
                    })}
                  </p>
                ) : (
                  <p className="text-xs text-gray-400 mt-1">
                    {t("paymentModule.userPaymentHistory.maximumLabel", {
                      max: formatAmount(
                        refundingPayment.amount,
                        refundingPayment.currency
                      ),
                    })}
                  </p>
                )}
              </div>
            )}

            <div>
              <FieldLabel
                htmlFor="refund-reason"
                value={
                  t(
                    "paymentModule.userPaymentHistory.reasonSelectLabel"
                  ) as string
                }
              />
              <Select
                id="refund-reason"
                color={
                  refundFormTouched && !refundReason ? "failure" : undefined
                }
                value={refundReason}
                onChange={(e) => setRefundReason(e.target.value)}
              >
                <option value="">
                  {t("paymentModule.userPaymentHistory.selectAReason")}
                </option>
                <option value="REQUESTED_BY_CUSTOMER">
                  {t(
                    "paymentModule.userPaymentHistory.reasonRequestedByCustomer"
                  )}
                </option>
                <option value="FRAUDULENT">
                  {t("paymentModule.userPaymentHistory.reasonFraudulent")}
                </option>
                <option value="DUPLICATE">
                  {t("paymentModule.userPaymentHistory.reasonDuplicate")}
                </option>
              </Select>
              {refundFormTouched && !refundReason && (
                <p className="mt-1 text-xs text-red-600">
                  {t("paymentModule.userPaymentHistory.reasonRequired")}
                </p>
              )}
            </div>
          </div>
        )}
      </ModalShell>
    </div>
  );
};

export type { CancelSubscriptionInfo };
export default UserPaymentHistory;
